import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useRelay } from "../lib/relayContext.tsx";
import {
  SEARCH_MAX_FILES,
  SEARCH_MAX_MATCHES,
  createSearchController,
  flattenResults,
  moveSelection,
  navigationTarget,
  type SearchItem,
  type SearchState,
} from "../lib/projectSearch.ts";
import type { RevealRange } from "../lib/useOpenFiles.ts";
import { ProjectSearchResults } from "./ProjectSearchResults.tsx";

export interface ProjectSearchProps {
  machineId: string;
  projectId: string;
  /** Changes every time the shortcut is pressed while the modal is already open: re-focus the input. */
  focusNonce: number;
  onClose: () => void;
  /** Opens/activates `path` (no duplicate tab) and, for content results, selects `range`. */
  onNavigate: (path: string, range: RevealRange | null) => void;
}

/** Global Project Search: one query over file names, relative paths and file contents. */
export function ProjectSearch({ machineId, projectId, focusNonce, onClose, onNavigate }: ProjectSearchProps) {
  const relay = useRelay();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<SearchState>({ status: "idle" });
  const [selected, setSelected] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, [focusNonce]);

  const controller = useMemo(
    () =>
      createSearchController({
        search: (q) =>
          relay.machine(machineId, "filesystem/searchProject", {
            projectId,
            query: q,
            maxFiles: SEARCH_MAX_FILES,
            maxMatches: SEARCH_MAX_MATCHES,
          }),
        onState: (s) => {
          setState(s);
          if (s.status === "done") setSelected(0);
        },
      }),
    [relay, machineId, projectId],
  );
  useEffect(() => () => controller.dispose(), [controller]);

  const shown =
    state.status === "done" ? state.response : state.status === "searching" ? state.previous : null;
  const items = useMemo(() => flattenResults(shown?.files ?? []), [shown]);

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [selected, items]);

  const open = (item: SearchItem | undefined) => {
    if (!item) return;
    const { path, range } = navigationTarget(item);
    onClose();
    onNavigate(path, range);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(moveSelection(s, e.key === "ArrowDown" ? 1 : -1, items.length), 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      open(items[selected]);
    }
  };

  return (
    <div
      className="dialog-backdrop project-search-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="project-search"
        role="dialog"
        aria-modal="true"
        aria-label="Search Project"
        data-testid="project-search"
        onKeyDown={onKeyDown}
      >
        <input
          ref={input}
          className="project-search-input"
          type="text"
          autoFocus
          spellCheck={false}
          autoComplete="off"
          placeholder="Search Project…"
          aria-label="Search Project"
          value={query}
          data-testid="project-search-input"
          onChange={(e) => {
            setQuery(e.target.value);
            controller.setQuery(e.target.value);
          }}
        />
        <div
          className="project-search-results"
          ref={list}
          role="listbox"
          data-testid="project-search-results"
        >
          {state.status === "idle" && (
            <p className="muted small project-search-note">Type to search file names, paths and contents.</p>
          )}
          {state.status === "error" && (
            <p className="error small project-search-note" data-testid="project-search-error">
              {state.message}
            </p>
          )}
          {state.status === "searching" && items.length === 0 && (
            <p className="muted small project-search-note">Searching…</p>
          )}
          {state.status === "done" && items.length === 0 && (
            <p className="muted small project-search-note" data-testid="project-search-empty">
              No results found
            </p>
          )}
          {state.status !== "error" && shown && (
            <ProjectSearchResults response={shown} items={items} selected={selected} onOpen={open} />
          )}
          {state.status !== "error" && shown?.truncated && (
            <p className="muted small project-search-note" data-testid="project-search-truncated">
              More results available — refine your search.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
